"use client";

import Image from "next/image";
import { Link2, Search, ShieldCheck } from "lucide-react";
import type { CSSProperties } from "react";

import { useCcLatchInView } from "@/lib/use-cc-latch-in-view";
import type { SeoWorkflowConnectCardCopy } from "./seo-workflow-copy";
import { CcSeoWorkflowCard } from "./CcSeoWorkflowCard";

function GlobeIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M3 12h18M12 3c2.5 2.8 3.8 6.2 3.8 9s-1.3 6.2-3.8 9M12 3c-2.5 2.8-3.8 6.2-3.8 9s1.3 6.2 3.8 9"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M5 12.5 9.5 17 19 7"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

type CcSeoWorkflowConnectProps = {
  copy: SeoWorkflowConnectCardCopy;
};

export function CcSeoWorkflowConnect({ copy }: CcSeoWorkflowConnectProps) {
  const { ref: panelRef, isInView } = useCcLatchInView<HTMLDivElement>({
    threshold: 0.35,
  });
  const isSearchMode = Boolean(copy.searchPrompt);
  const fieldText = copy.searchPrompt ?? copy.demoUrl ?? "captivateclick.com";
  const activeFlowsCount = copy.activeFlowsCount ?? "142";
  const integrations = copy.integrations ?? [];

  return (
    <CcSeoWorkflowCard
      id="connect"
      title={copy.title}
      description={copy.description}
      badge={copy.badge}
      icon={Link2}
    >
      <div
        className={`cc-seo-workflow-connect${isSearchMode ? " cc-seo-workflow-connect--search" : ""}`}
      >
        <div
          ref={panelRef}
          className={[
            "cc-seo-workflow-connect__panel",
            isInView && "cc-seo-workflow-connect__panel--in-view",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          <div className="cc-seo-workflow-connect__form">
            <span className="cc-seo-workflow-connect__icon" aria-hidden="true">
              {isSearchMode ? <Search size={14} strokeWidth={1.75} /> : <GlobeIcon />}
            </span>
            <div className="cc-seo-workflow-connect__form-body">
              <div className="cc-seo-workflow-connect__field">
                {!isSearchMode ? (
                  <span className="cc-seo-workflow-connect__prefix">https://</span>
                ) : null}
                <span
                  className={`cc-seo-workflow-connect__url${isSearchMode ? " cc-seo-workflow-connect__url--prompt" : ""}`}
                >
                  {fieldText}
                </span>
                <span className="cc-seo-workflow-connect__cursor" aria-hidden="true" />
              </div>
              <span className="cc-seo-workflow-connect__pill">
                <span className="cc-seo-workflow-connect__pill-dot" aria-hidden="true" />
                {copy.analyzing}
              </span>
            </div>
          </div>

          {integrations.length > 0 ? (
            <>
              <div className="cc-seo-workflow-connect__divider" aria-hidden="true" />
              <ul className="cc-seo-workflow-connect__integrations" aria-hidden="true">
                {integrations.map((item, index) => (
                  <li
                    key={item.id}
                    className="cc-seo-workflow-connect__integration"
                    style={{ "--connect-integration-index": index } as CSSProperties}
                  >
                    <span className="cc-seo-workflow-connect__integration-brand">
                      {item.logo ? (
                        <Image
                          className="cc-seo-workflow-connect__integration-logo"
                          src={item.logo}
                          alt=""
                          width={18}
                          height={18}
                          unoptimized
                        />
                      ) : (
                        <span className="cc-seo-workflow-connect__integration-badge">
                          {item.name.charAt(0)}
                        </span>
                      )}
                      <span className="cc-seo-workflow-connect__integration-name">{item.name}</span>
                    </span>
                    <span className="cc-seo-workflow-connect__integration-status">
                      <span className="cc-seo-workflow-connect__check" aria-hidden="true">
                        <CheckIcon />
                      </span>
                      {item.status}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          ) : null}

          <div className="cc-seo-workflow-connect__divider" aria-hidden="true" />

          <ul className="cc-seo-workflow-connect__stats">
            <li
              className="cc-seo-workflow-connect__stat"
              style={{ "--connect-stat-index": 0 } as CSSProperties}
            >
              <span className="cc-seo-workflow-connect__stat-label">{copy.siteHealth}</span>
              <span className="cc-seo-workflow-connect__stat-value">
                <span className="cc-seo-workflow-connect__check" aria-hidden="true">
                  <CheckIcon />
                </span>
                {copy.good}
              </span>
            </li>
            <li
              className="cc-seo-workflow-connect__stat"
              style={{ "--connect-stat-index": 1 } as CSSProperties}
            >
              <span className="cc-seo-workflow-connect__stat-label">{copy.pagesIndexed}</span>
              <span className="cc-seo-workflow-connect__stat-meta">
                <span
                  className="cc-seo-workflow-connect__bar cc-seo-workflow-connect__bar--indexed"
                  aria-hidden="true"
                >
                  <span className="cc-seo-workflow-connect__bar-fill" />
                </span>
                <span className="cc-seo-workflow-connect__stat-value">{activeFlowsCount}</span>
              </span>
            </li>
            <li
              className="cc-seo-workflow-connect__stat"
              style={{ "--connect-stat-index": 2 } as CSSProperties}
            >
              <span className="cc-seo-workflow-connect__stat-label">{copy.coreWebVitals}</span>
              <span className="cc-seo-workflow-connect__stat-meta">
                <span
                  className="cc-seo-workflow-connect__bar cc-seo-workflow-connect__bar--vitals"
                  aria-hidden="true"
                >
                  <span className="cc-seo-workflow-connect__bar-fill" />
                </span>
                <span className="cc-seo-workflow-connect__stat-value">
                  <span className="cc-seo-workflow-connect__check" aria-hidden="true">
                    <CheckIcon />
                  </span>
                  {copy.pass}
                </span>
              </span>
            </li>
          </ul>

          <div className="cc-seo-workflow-connect__footer" aria-hidden="true">
            <ShieldCheck size={12} strokeWidth={1.5} aria-hidden="true" />
            <span>{copy.connectedVia}</span>
          </div>
        </div>
      </div>
    </CcSeoWorkflowCard>
  );
}
